<template>
  <div class="streaming-output-input-manager">
    <!-- Output Management -->
    <div class="output-section mb-8">
      <h2 class="text-lg font-semibold text-white mb-4">
        Output Configuration
      </h2>
      
      <div class="bg-gray-800 rounded-xs border border-gray-700 p-6">
        <!-- Output Type Selection -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-400 mb-2">
            Output Type
          </label>
          <select
            v-model="outputForm.outputType"
            class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            @change="onOutputTypeChange"
          >
            <option value="">Select Output Type</option>
            <option value="ROUTE">ROUTE (Stream Router)</option>
            <option value="OTT">OTT (Wowza)</option>
          </select>
        </div>

        <!-- ROUTE Output Configuration -->
        <div v-if="outputForm.outputType === 'ROUTE'" class="route-config space-y-4">
          <h3 class="text-md font-medium text-gray-200">
            Stream Router Configuration
          </h3>
          
          <!-- Stream Router Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">
              Stream Router
            </label>
            <select
              v-model="routeConfig.streamRouterId"
              class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white"
              @change="loadRouterDestinations"
            >
              <option value="">Select Stream Router</option>
              <option v-for="router in streamRouters" :key="router.id" :value="router.id">
                {{ router.name }} ({{ router.description }})
              </option>
            </select>
          </div>

          <!-- Interface Destination Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">
              Interface Destination
              <span class="text-xs text-gray-500">(Required for destination.url)</span>
            </label>
            <select
              v-model="routeConfig.selectedDestination"
              class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white"
              :disabled="!routeConfig.streamRouterId"
            >
              <option value="">Select Interface Destination</option>
              <option
                v-for="dest in routerDestinations"
                :key="dest.id"
                :value="dest"
              >
                {{ dest.name }} - {{ dest.url }}
              </option>
            </select>
          </div>

          <!-- Interface Configuration -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                Interface Name
              </label>
              <input
                v-model="routeConfig.interfaceName"
                type="text"
                class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white placeholder-gray-400"
                placeholder="Interface name"
              />
            </div>
            
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                Source URL
              </label>
              <input
                v-model="routeConfig.sourceUrl"
                type="text"
                class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white placeholder-gray-400"
                placeholder="srt://source:port"
              />
            </div>
          </div>

          <div class="flex items-center">
            <input
              v-model="routeConfig.autostart"
              type="checkbox"
              id="autostart"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 bg-gray-700 rounded-xs"
            />
            <label for="autostart" class="ml-2 text-sm text-gray-300">
              Auto-start Interface
            </label>
          </div>
        </div>

        <!-- OTT Output Configuration -->
        <div v-if="outputForm.outputType === 'OTT'" class="ott-config space-y-4">
          <h3 class="text-md font-medium text-gray-200">
            Wowza OTT Configuration
          </h3>
          
          <!-- Wowza Server Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">
              Wowza Server
            </label>
            <select
              v-model="ottConfig.ottServer"
              class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white"
              @change="loadWowzaApplications"
            >
              <option value="">Select Wowza Server</option>
              <option
                v-for="server in wowzaServers"
                :key="server.equipmentId"
                :value="server.equipmentId"
              >
                {{ server.name }} ({{ server.equipmentId }})
              </option>
            </select>
          </div>

          <!-- Wowza Application Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">
              Wowza Application
            </label>
            <select
              v-model="ottConfig.ottApplication"
              class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white"
              :disabled="!ottConfig.ottServer"
              @change="loadWowzaTargets"
            >
              <option value="">Select Application</option>
              <option
                v-for="app in wowzaApplications"
                :key="app.id"
                :value="app.id"
              >
                {{ app.name }} - {{ app.description }}
              </option>
            </select>
          </div>

          <!-- Target Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">
              Target
              <span class="text-xs text-gray-500">(Name will be set automatically)</span>
            </label>
            <select
              v-model="ottConfig.selectedTarget"
              class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white"
              :disabled="!ottConfig.ottApplication"
              @change="onTargetSelect"
            >
              <option value="">Select Target</option>
              <option
                v-for="target in wowzaTargets"
                :key="target.id"
                :value="target"
              >
                {{ target.name }}
              </option>
            </select>
          </div>

          <!-- Platform Selection -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                OTT Platform
              </label>
              <select
                v-model="ottConfig.ottPlatform"
                class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white"
              >
                <option value="">Select Platform</option>
                <option value="YOUTUBE">YouTube</option>
                <option value="FACEBOOK">Facebook</option>
                <option value="TWITCH">Twitch</option>
                <option value="RTMP">RTMP</option>
                <option value="HLS">HLS</option>
                <option value="GENERIC">Generic</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-300 mb-2">
                Default Transcoder Profile
              </label>
              <select
                v-model="ottConfig.ottDefaultTranscoderProfile"
                class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white"
                :disabled="!ottConfig.ottApplication"
              >
                <option value="">Select Profile</option>
                <option
                  v-for="template in transcoderTemplates"
                  :key="template.id"
                  :value="template.name"
                >
                  {{ template.name }} - {{ template.description }}
                </option>
              </select>
            </div>
          </div>

          <div class="flex items-center">
            <input
              v-model="ottConfig.ottNoStremKeyRequired"
              type="checkbox"
              id="noStreamKey"
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 bg-gray-700 rounded-xs"
            />
            <label for="noStreamKey" class="ml-2 text-sm text-gray-300">
              No Stream Key Required
            </label>
          </div>

          <!-- New Target Creation -->
          <div v-if="showNewTargetForm" class="mt-6 p-4 border border-gray-600 rounded-xs bg-gray-700">
            <h4 class="text-sm font-medium text-gray-200 mb-3">
              Create New Target
            </h4>
            
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">
                  Source Stream Name
                </label>
                <input
                  v-model="newTargetConfig.sourceStreamName"
                  type="text"
                  class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white placeholder-gray-400"
                  placeholder="DAZN_HD"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">
                  Stream Name
                </label>
                <input
                  v-model="newTargetConfig.streamName"
                  type="text"
                  class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white placeholder-gray-400"
                  placeholder="live2"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">
                  Host
                </label>
                <input
                  v-model="newTargetConfig.host"
                  type="text"
                  class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white placeholder-gray-400"
                  placeholder="a.rtmp.youtube.com"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">
                  Port
                </label>
                <input
                  v-model="newTargetConfig.port"
                  type="number"
                  class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white placeholder-gray-400"
                  placeholder="1935"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-between items-center mt-6 pt-6 border-t border-gray-700">
          <div class="flex gap-2">
            <button
              v-if="outputForm.outputType === 'OTT'"
              @click="showNewTargetForm = !showNewTargetForm"
              class="px-3 py-1.5 text-sm font-medium text-gray-300 bg-gray-600 border border-gray-500 rounded-xs hover:bg-gray-500"
            >
              {{ showNewTargetForm ? 'Cancel New Target' : 'Create New Target' }}
            </button>
            
            <button
              v-if="createdInterfaces.length > 0 || createdTargets.length > 0"
              @click="cleanupTestResources"
              class="px-3 py-1.5 text-sm font-medium text-red-400 bg-red-900/20 border border-red-700 rounded-xs hover:bg-red-900/30"
            >
              Cleanup Test Resources
            </button>
          </div>

          <div class="flex gap-2">
            <button
              @click="resetForm"
              class="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-xs hover:bg-gray-600"
            >
              Reset
            </button>
            
            <button
              @click="saveOutput"
              :disabled="!isValidConfig || saving"
              class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-xs hover:bg-blue-700 focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ saving ? 'Saving...' : 'Save Output' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Input Management -->
    <div class="input-section">
      <h2 class="text-lg font-semibold text-white mb-4">
        Input Configuration (Stream Router Interface)
      </h2>
      
      <div class="bg-gray-800 rounded-xs border border-gray-700 p-6">
        <!-- Interface Destination Selection for Input -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-300 mb-2">
            Interface Destination
            <span class="text-xs text-gray-500">(Required for destination.url in Input)</span>
          </label>
          <select
            v-model="inputConfig.selectedDestination"
            class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white"
          >
            <option value="">Select Interface Destination</option>
            <option
              v-for="dest in routerDestinations"
              :key="dest.id"
              :value="dest"
            >
              {{ dest.name }} - {{ dest.url }}
            </option>
          </select>
        </div>

        <!-- Input Configuration -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">
              Input Name
            </label>
            <input
              v-model="inputConfig.name"
              type="text"
              class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white placeholder-gray-400"
              placeholder="Input name"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-300 mb-2">
              Description
            </label>
            <input
              v-model="inputConfig.description"
              type="text"
              class="w-full px-3 py-2 border border-gray-600 rounded-xs bg-gray-700 text-white placeholder-gray-400"
              placeholder="Input description"
            />
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-end gap-2 pt-4 border-t border-gray-700">
          <button
            @click="resetInputForm"
            class="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-xs hover:bg-gray-600"
          >
            Reset
          </button>
          
          <button
            @click="saveInput"
            :disabled="!isValidInputConfig || savingInput"
            class="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-xs hover:bg-green-700 focus:outline-hidden focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ savingInput ? 'Saving...' : 'Save Input' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Debug Information (Development Mode) -->
    <div v-if="isDevelopment" class="debug-section mt-8">
      <h3 class="text-md font-medium text-gray-200 mb-4">
        Debug Information
      </h3>
      
      <div class="bg-gray-800 rounded-xs p-4 border border-gray-700">
        <details>
          <summary class="cursor-pointer text-sm font-medium text-gray-300 mb-2">
            Current Configuration
          </summary>
          <pre class="text-xs text-gray-400 mt-2">{{ JSON.stringify({ outputForm, routeConfig, ottConfig, inputConfig }, null, 2) }}</pre>
        </details>
      </div>
    </div>
  </div>
</template>

<script>
import { generateClient } from 'aws-amplify/api';
import * as streamRouterMutations from '@/graphql/streamRouterMutations';
import * as qlmutations from '@/graphql/mutations';
import * as qlqueries from '@/graphql/queries';

const client = generateClient();
const graphqlOperation = (query, variables) => ({ query, variables });

export default {
  name: 'StreamingOutputInputManager',
  data() {
    return {
      // Loading states
      saving: false,
      savingInput: false,
      loading: false,
      
      // Form data
      outputForm: {
        outputType: '',
        name: '',
        description: '',
        smEndpoints: '',
        config: ''
      },
      
      // ROUTE configuration
      routeConfig: {
        streamRouterId: '',
        interfaceName: '',
        sourceUrl: '',
        selectedDestination: null,
        autostart: true
      },
      
      // OTT configuration
      ottConfig: {
        ottServer: '',
        ottApplication: '',
        ottPlatform: '',
        ottDefaultTranscoderProfile: '',
        ottNoStremKeyRequired: false,
        selectedTarget: null
      },
      
      // Input configuration
      inputConfig: {
        name: '',
        description: '',
        selectedDestination: null
      },
      
      // New target creation
      showNewTargetForm: false,
      newTargetConfig: {
        sourceStreamName: '',
        streamName: '',
        host: '',
        port: 1935
      },
      
      // Data lists
      streamRouters: [],
      routerDestinations: [],
      wowzaServers: [],
      wowzaApplications: [],
      wowzaTargets: [],
      transcoderTemplates: [],
      
      // Test cleanup tracking
      createdInterfaces: [],
      createdTargets: []
    };
  },
  
  computed: {
    isDevelopment() {
      return process.env.NODE_ENV === 'development';
    },
    
    isValidConfig() {
      if (this.outputForm.outputType === 'ROUTE') {
        return this.routeConfig.streamRouterId && 
               this.routeConfig.selectedDestination && 
               this.routeConfig.interfaceName &&
               this.routeConfig.sourceUrl;
      } else if (this.outputForm.outputType === 'OTT') {
        return this.ottConfig.ottServer && 
               this.ottConfig.ottApplication && 
               this.ottConfig.selectedTarget &&
               this.ottConfig.ottPlatform;
      }
      return false;
    },
    
    isValidInputConfig() {
      return this.inputConfig.selectedDestination && 
             this.inputConfig.name && 
             this.inputConfig.description;
    }
  },
  
  async mounted() {
    await this.loadInitialData();
  },
  
  methods: {
    async loadInitialData() {
      this.loading = true;
      try {
        await Promise.all([
          this.loadStreamRouters(),
          this.loadWowzaServers()
        ]);
      } catch (error) {
        console.error('Failed to load initial data:', error);
        this.$message.error({ message: 'Failed to load initial data' });
      } finally {
        this.loading = false;
      }
    },
    
    async loadStreamRouters() {
      try {
        // Load stream routers - assuming they're stored as routers with specific type
        const result = await client.graphql(
          graphqlOperation(qlqueries.listRouters, { limit: 100 })
        );
        
        this.streamRouters = result.data.listRouters.items || [];
        console.log('Loaded stream routers:', this.streamRouters.length);
      } catch (error) {
        console.error('Failed to load stream routers:', error);
      }
    },
    
    async loadRouterDestinations() {
      if (!this.routeConfig.streamRouterId) return;
      
      try {
        const result = await client.graphql(
          graphqlOperation(qlqueries.listRouterDestinations, {
            filter: {
              routerId: { eq: this.routeConfig.streamRouterId }
            },
            limit: 100
          })
        );
        
        this.routerDestinations = result.data.listRouterDestinations.items || [];
        console.log('Loaded router destinations:', this.routerDestinations.length);
      } catch (error) {
        console.error('Failed to load router destinations:', error);
        this.$message.error({ message: 'Failed to load router destinations' });
      }
    },
    
    async loadWowzaServers() {
      try {
        // Load equipment with type WOWZA
        const result = await client.graphql(
          graphqlOperation(streamRouterMutations.listEquipmentWowza, {
            filter: {
              equipmentType: { eq: 'WOWZA' }
            },
            limit: 100
          })
        );
        
        this.wowzaServers = result.data.listEquipment.items || [];
        console.log('Loaded Wowza servers:', this.wowzaServers.length);
      } catch (error) {
        console.error('Failed to load Wowza servers:', error);
      }
    },
    
    async loadWowzaApplications() {
      if (!this.ottConfig.ottServer) return;
      
      try {
        const result = await client.graphql(
          graphqlOperation(streamRouterMutations.listWowzaApplications, {
            serverId: this.ottConfig.ottServer
          })
        );
        
        if (result.data.listWowzaApplications.success) {
          this.wowzaApplications = result.data.listWowzaApplications.data.applications || [];
          console.log('Loaded Wowza applications:', this.wowzaApplications.length);
        }
      } catch (error) {
        console.error('Failed to load Wowza applications:', error);
        this.$message.error({ message: 'Failed to load Wowza applications' });
      }
    },
    
    async loadWowzaTargets() {
      if (!this.ottConfig.ottServer || !this.ottConfig.ottApplication) return;
      
      try {
        const [targetsResult, templatesResult] = await Promise.all([
          client.graphql(
            graphqlOperation(streamRouterMutations.listWowzaAppTargets, {
              serverId: this.ottConfig.ottServer,
              applicationId: this.ottConfig.ottApplication
            })
          ),
          client.graphql(
            graphqlOperation(streamRouterMutations.listTranscoderTemplates, {
              serverId: this.ottConfig.ottServer,
              applicationId: this.ottConfig.ottApplication
            })
          )
        ]);
        
        if (targetsResult.data.listWowzaAppTargets.success) {
          this.wowzaTargets = targetsResult.data.listWowzaAppTargets.data.targets || [];
        }
        
        if (templatesResult.data.listTranscoderTemplates.success) {
          this.transcoderTemplates = templatesResult.data.listTranscoderTemplates.data.templates || [];
        }
        
        console.log('Loaded targets and templates:', this.wowzaTargets.length, this.transcoderTemplates.length);
      } catch (error) {
        console.error('Failed to load Wowza targets/templates:', error);
        this.$message.error({ message: 'Failed to load Wowza targets or templates' });
      }
    },
    
    onOutputTypeChange() {
      // Reset configurations when output type changes
      this.routeConfig = {
        streamRouterId: '',
        interfaceName: '',
        sourceUrl: '',
        selectedDestination: null,
        autostart: true
      };
      
      this.ottConfig = {
        ottServer: '',
        ottApplication: '',
        ottPlatform: '',
        ottDefaultTranscoderProfile: '',
        ottNoStremKeyRequired: false,
        selectedTarget: null
      };
      
      this.showNewTargetForm = false;
    },
    
    onTargetSelect() {
      // Automatically set name to target name
      if (this.ottConfig.selectedTarget) {
        this.outputForm.name = this.ottConfig.selectedTarget.name;
      }
    },
    
    async saveOutput() {
      if (!this.isValidConfig) return;
      
      this.saving = true;
      
      try {
        if (this.outputForm.outputType === 'ROUTE') {
          await this.saveRouteOutput();
        } else if (this.outputForm.outputType === 'OTT') {
          await this.saveOTTOutput();
        }
        
        this.$message.success({ message: 'Output saved successfully' });
        this.resetForm();
      } catch (error) {
        console.error('Failed to save output:', error);
        this.$message.error({ message: 'Failed to save output' });
      } finally {
        this.saving = false;
      }
    },
    
    async saveRouteOutput() {
      // 1. Create Stream Router Interface
      const interfaceData = {
        name: this.routeConfig.interfaceName,
        source: {
          url: this.routeConfig.sourceUrl,
          type: 'srt',
          max_connections: 1
        },
        destination: {
          url: this.routeConfig.selectedDestination.url, // Required from selected destination
          type: 'srt',
          max_connections: 1
        },
        processors: [],
        tags: [],
        whitelist: [],
        autostart: this.routeConfig.autostart
      };
      
      console.log('Creating Stream Router Interface:', { 
        streamRouterId: this.routeConfig.streamRouterId, 
        interfaceData 
      });
      
      const interfaceResult = await client.graphql(
        graphqlOperation(streamRouterMutations.createStreamRouterInterface, {
          streamRouterId: this.routeConfig.streamRouterId,
          interfaceData: interfaceData
        })
      );
      
      if (!interfaceResult.data.createStreamRouterInterface.success) {
        throw new Error(interfaceResult.data.createStreamRouterInterface.message);
      }
      
      const interfaceResponse = interfaceResult.data.createStreamRouterInterface.data;
      
      // Track for cleanup
      this.createdInterfaces.push({
        streamRouterId: this.routeConfig.streamRouterId,
        interfaceId: interfaceResponse.interfaceId
      });
      
      // 2. Update Output with SR data
      const smEndpoints = JSON.stringify([
        interfaceResponse.source,
        interfaceResponse.destination
      ]);
      
      const config = JSON.stringify({
        interfaceId: interfaceResponse.interfaceId,
        interfaceName: interfaceResponse.name,
        streamManagerId: interfaceResponse.streamManagerId,
        interfaceDescription: interfaceResponse.description || '',
        type: 'SR'
      });
      
      this.outputForm.smEndpoints = smEndpoints;
      this.outputForm.config = config;
      
      console.log('ROUTE Output configured:', { smEndpoints, config });
    },
    
    async saveOTTOutput() {
      // 1. Create new target if requested
      if (this.showNewTargetForm && this.newTargetConfig.sourceStreamName) {
        const targetConfig = {
          entryName: `${new Date().toISOString()}_target`,
          enabled: true,
          sourceStreamName: this.newTargetConfig.sourceStreamName,
          streamName: this.newTargetConfig.streamName,
          host: this.newTargetConfig.host,
          port: this.newTargetConfig.port
        };
        
        console.log('Creating new Wowza target:', targetConfig);
        
        const targetResult = await client.graphql(
          graphqlOperation(streamRouterMutations.createExternalWowzaTarget, {
            serverId: this.ottConfig.ottServer,
            applicationId: this.ottConfig.ottApplication,
            targetConfig: targetConfig
          })
        );
        
        if (!targetResult.data.createExternalWowzaTarget.success) {
          throw new Error(targetResult.data.createExternalWowzaTarget.message);
        }
        
        // Track for cleanup
        this.createdTargets.push({
          serverId: this.ottConfig.ottServer,
          applicationId: this.ottConfig.ottApplication,
          targetId: targetResult.data.createExternalWowzaTarget.data.targetId
        });
      }
      
      // 2. Configure OTT Output
      const config = JSON.stringify({
        server: this.ottConfig.ottServer,
        application: this.ottConfig.ottApplication,
        target: this.ottConfig.selectedTarget.id || this.ottConfig.selectedTarget.name,
        platform: this.ottConfig.ottPlatform,
        defaultTranscoderProfile: this.ottConfig.ottDefaultTranscoderProfile,
        streamKeyRequired: !this.ottConfig.ottNoStremKeyRequired
      });
      
      this.outputForm.config = config;
      
      console.log('OTT Output configured:', { config });
    },
    
    async saveInput() {
      if (!this.isValidInputConfig) return;
      
      this.savingInput = true;
      
      try {
        // Create Stream Router Interface for Input
        const interfaceData = {
          name: this.inputConfig.name,
          source: {
            url: 'srt://input:port', // Placeholder - adjust as needed
            type: 'srt'
          },
          destination: {
            url: this.inputConfig.selectedDestination.url, // Required from selected destination
            type: 'srt'
          },
          processors: [],
          tags: [],
          whitelist: [],
          autostart: true
        };
        
        console.log('Creating Input Interface:', interfaceData);
        
        const result = await client.graphql(
          graphqlOperation(streamRouterMutations.createStreamRouterInterface, {
            streamRouterId: this.routeConfig.streamRouterId || this.streamRouters[0]?.id,
            interfaceData: interfaceData
          })
        );
        
        if (!result.data.createStreamRouterInterface.success) {
          throw new Error(result.data.createStreamRouterInterface.message);
        }
        
        // Track for cleanup
        this.createdInterfaces.push({
          streamRouterId: this.routeConfig.streamRouterId || this.streamRouters[0]?.id,
          interfaceId: result.data.createStreamRouterInterface.data.interfaceId
        });
        
        this.$message.success({ message: 'Input saved successfully' });
        this.resetInputForm();
      } catch (error) {
        console.error('Failed to save input:', error);
        this.$message.error({ message: 'Failed to save input' });
      } finally {
        this.savingInput = false;
      }
    },
    
    async cleanupTestResources() {
      try {
        const cleanup = [];
        
        // Delete created interfaces
        for (const iface of this.createdInterfaces) {
          cleanup.push(
            client.graphql(
              graphqlOperation(streamRouterMutations.deleteStreamRouterInterface, {
                streamRouterId: iface.streamRouterId,
                interfaceId: iface.interfaceId
              })
            )
          );
        }
        
        // Note: Target cleanup would need a delete mutation that might not exist
        // For now, just log the targets that were created
        if (this.createdTargets.length > 0) {
          console.log('Created targets (manual cleanup may be needed):', this.createdTargets);
        }
        
        await Promise.all(cleanup);
        
        this.createdInterfaces = [];
        this.createdTargets = [];
        
        this.$message.success({ message: 'Test resources cleaned up' });
      } catch (error) {
        console.error('Failed to cleanup test resources:', error);
        this.$message.error({ message: 'Failed to cleanup all test resources' });
      }
    },
    
    resetForm() {
      this.outputForm = {
        outputType: '',
        name: '',
        description: '',
        smEndpoints: '',
        config: ''
      };
      
      this.routeConfig = {
        streamRouterId: '',
        interfaceName: '',
        sourceUrl: '',
        selectedDestination: null,
        autostart: true
      };
      
      this.ottConfig = {
        ottServer: '',
        ottApplication: '',
        ottPlatform: '',
        ottDefaultTranscoderProfile: '',
        ottNoStremKeyRequired: false,
        selectedTarget: null
      };
      
      this.showNewTargetForm = false;
      this.newTargetConfig = {
        sourceStreamName: '',
        streamName: '',
        host: '',
        port: 1935
      };
    },
    
    resetInputForm() {
      this.inputConfig = {
        name: '',
        description: '',
        selectedDestination: null
      };
    }
  }
};
</script>

<style scoped>
/* Component-specific styles */
.streaming-output-input-manager {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
}

/* Custom focus styles for better accessibility */
.focus\:outline-hidden:focus {
  outline: 2px solid transparent;
}

/* Validation styling */
.border-red-300 {
  border-color: #fca5a5;
}

.dark .border-red-700 {
  border-color: #b91c1c;
}

/* Loading state */
.disabled\:opacity-50:disabled {
  opacity: 0.5;
}

.disabled\:cursor-not-allowed:disabled {
  cursor: not-allowed;
}
</style>
